import { TZDate } from '@date-fns/tz';
import {
  deviceTimeZone,
  isPickableTimeZone,
  timeZoneLabel,
  timeZoneShortLabel,
} from '@lib/social/scheduling/timeZone';
import { action, computed, makeObservable, observable } from 'mobx';

const TIME_ZONE_KEY = 'schedule.timeZone';

/** After the zone's label while the schedule follows the device's clock: a pinned zone stays when the laptop travels, this one does not. */
const FOLLOWS_DEVICE_NOTE = ' · your device';

/**
 * How this device shows the schedule. Today that is the time zone the
 * schedule surfaces speak in: the compose picker, the post times, and the
 * day math behind the calendar and the agenda. Someone in Bucharest
 * scheduling for a Los Angeles audience pins it to LA and types 6 PM as 6 PM.
 * Further options for these surfaces land here, one localStorage key each.
 *
 * Deliberately **device-level, not account-level**, like
 * `LoginPreferencesStore`: it lives in localStorage and is never written to
 * the user's settings. `null` means "follow the device's clock", which is
 * what everyone has until they pick. A stored name the dialog could not
 * show as picked (`isPickableTimeZone`: another browser's alias, or
 * hand-edited) reads as `null`.
 *
 * A singleton so both React components and MobX computeds read it reactively.
 */
export class ScheduleSettingsStore {
  @observable
  private _pinnedTimeZone: string | null;

  constructor() {
    const stored = localStorage.getItem(TIME_ZONE_KEY);
    this._pinnedTimeZone = stored && isPickableTimeZone(stored) ? stored : null;
    makeObservable(this);
  }

  /** The pinned zone, or null while following the device. */
  @computed
  get pinnedTimeZone(): string | null {
    return this._pinnedTimeZone;
  }

  /** The zone in effect: the pinned one, else the device's. */
  @computed
  get timeZone(): string {
    return this._pinnedTimeZone ?? deviceTimeZone();
  }

  /** Pin a zone, or `null` to follow the device again. Pinning the device's own zone is a pin: it stays when the clock moves. */
  @action
  pinTimeZone(zone: string | null) {
    this._pinnedTimeZone = zone;
    if (zone === null) localStorage.removeItem(TIME_ZONE_KEY);
    else localStorage.setItem(TIME_ZONE_KEY, zone);
  }

  /**
   * The zone in effect, worded for a surface: `America/Los Angeles (GMT-7)`
   * in full, `Los Angeles (GMT-7)` short, each followed by `· your device`
   * while following the clock. The one place that sentence is composed.
   */
  timeZoneLabel(at: number, style: 'full' | 'short' = 'full'): string {
    const zone = this.timeZone;
    const label = style === 'full' ? timeZoneLabel(zone, at) : timeZoneShortLabel(zone, at);
    return this._pinnedTimeZone === null ? `${label}${FOLLOWS_DEVICE_NOTE}` : label;
  }

  /**
   * An instant as a date in the zone in effect: date-fns reads and writes a
   * `TZDate`'s fields in its zone and keeps the zone through its day math,
   * so a `now` or a `publishAt` built here makes `startOfDay`, `format` and
   * the rest answer in the schedule's zone. The one way the schedule
   * surfaces build a date.
   */
  inZone(ms: number): TZDate {
    return new TZDate(ms, this.timeZone);
  }
}

export const scheduleSettings = new ScheduleSettingsStore();
